import req from '../../api/request'
import type { UpdatePlayerPayload } from '../player/playerProfile'
import type { RosterPlayerListItem } from '../../features/team/player/mapRosterPlayerToForm'

/**
 * GET /teams/:teamId/rosters/:rosterId/players/:playerId — fetch ONE roster
 * player. Returns the same shape as an entry of the roster list's `players[]`,
 * so `mapRosterPlayerToForm` consumes it unchanged.
 */
export const getRosterPlayer = async (
  teamId: string,
  rosterId: string,
  playerId: string
): Promise<RosterPlayerListItem> => {
  const { data } = await req(
    `/teams/${teamId}/rosters/${rosterId}/players/${playerId}`
  )
  return data as RosterPlayerListItem
}

/**
 * POST body for adding a player to a roster. The personal fields reuse the exact
 * shape `UpdatePlayerPayload` defines for PUT /users/me/players/:id (the team
 * roster endpoint now accepts the same player properties); the roster fields use
 * the legacy roster keys.
 */
export type RosterCreatePayload = UpdatePlayerPayload & {
  name_first?: string
  name_last?: string
  player_number?: string
  player_type_id?: number
  is_injured?: boolean
  is_affiliate?: boolean
  /** True on add (create/copy) so the player joins the active/playing roster. */
  is_playing?: boolean
  customer_identifier?: string
  /** Salesforce ID — Canlan only. */
  sf_id?: string
}

/** POST /teams/:teamId/rosters/:rosterId/players — add a player to a roster. */
export const createRosterPlayer = async (
  teamId: string,
  rosterId: string,
  body: RosterCreatePayload
): Promise<{ id: string } & Record<string, unknown>> => {
  const { data } = await req(`/teams/${teamId}/rosters/${rosterId}/players`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeout: 30000,
  })
  return data as { id: string } & Record<string, unknown>
}

/**
 * PUT /teams/:teamId/rosters/:rosterId/players/:playerId/copy — add an EXISTING
 * player (found via search) to a roster. Now accepts the full player payload
 * (same shape as create) so the prefilled personal fields + roster info are all
 * sent.
 */
export const copyRosterPlayer = async (
  teamId: string,
  rosterId: string,
  playerId: string,
  body: RosterCreatePayload
): Promise<{ id: string } & Record<string, unknown>> => {
  const { data } = await req(
    `/teams/${teamId}/rosters/${rosterId}/players/${playerId}/copy`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      timeout: 30000,
    }
  )
  return data as { id: string } & Record<string, unknown>
}

/**
 * PUT /teams/:teamId/rosters/:rosterId/players/:playerId — edit an existing
 * roster player. Sends the full player payload (personal + roster fields); image
 * is handled separately via the image endpoints below.
 */
export const updateRosterPlayer = async (
  teamId: string,
  rosterId: string,
  playerId: string,
  body: RosterCreatePayload
): Promise<{ id: string } & Record<string, unknown>> => {
  const { data } = await req(
    `/teams/${teamId}/rosters/${rosterId}/players/${playerId}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      timeout: 30000,
    }
  )
  return data as { id: string } & Record<string, unknown>
}

/** POST /teams/:teamId/players/:playerId/image — upload avatar (legacy team route). */
export const uploadRosterPlayerImage = async (
  teamId: string,
  playerId: string,
  file: File | string
): Promise<unknown> => {
  return req(`/teams/${teamId}/players/${playerId}/image`, {
    method: 'POST',
    body: file,
  })
}

/** DELETE /teams/:teamId/players/:playerId/image — delete avatar (edit-next). */
export const deleteRosterPlayerImage = async (
  teamId: string,
  playerId: string
): Promise<unknown> => {
  return req(`/teams/${teamId}/players/${playerId}/image`, {
    method: 'DELETE',
  })
}
